import React, { useCallback, useMemo, useRef } from 'react';
import {
    Animated,
    CellRendererProps,
    Platform,
    RefreshControl,
    StyleSheet,
    View,
    useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { NativeStackScreenProps } from '@react-navigation/native-stack';

import { PickInfoFragment } from '@/api/entries/query.generated';
import Gradient from '@/assets/icons/gradient';
import { KeyboardAvoidingViewNoAnimation } from '@/components/KeyboardAvoidingViewNoAnimation';
import { SheetScreen } from '@/components/ScreenComponent';
import { SizedBox } from '@/components/SizedBox';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { DynamicPayoutSheet } from '@/feature/betslip-pickem/components/DynamicPayoutSheet';
import { playersHaveEdgeCombo } from '@/feature/betslip-pickem/utils/betslip-edge-combos';
import { eventUtils } from '@/feature/betslip-pickem/utils/event-utils';
import { EntryActions } from '@/feature/entries-pickem/components/EntryActions';
import { EntryHeaderActions } from '@/feature/entries-pickem/components/EntryHeaderActions';
import { EntryPickPlayerCard } from '@/feature/entries-pickem/components/EntryPickPlayerCard';
import { EntrySectionEventHeader } from '@/feature/entries-pickem/components/EntrySectionEventHeader';
import { isEntryWin } from '@/feature/entries-pickem/entries-utils';
import { useEntryDetails } from '@/feature/entries-pickem/hooks/use-entry-details';
import { usePickemCashedOutEntries } from '@/feature/entries-pickem/hooks/use-pickem-cashed-out-entries';
import { getRadialGradientStyle, gradientWidth } from '@/feature/entry-share/utils/get-radial-gradient-style';
import { useLobbySectionSelection } from '@/feature/lobby/hooks/use-lobby-section-selection';
import {
    CellRendererComponent as CellRendererComponentForWeb,
    webListContentContainerStyle,
    webMaxWidthStyle,
} from '@/feature/responsive-design/WebComponents';
import { useResumeEffect } from '@/hooks/use-resume';
import { RootStackParamList } from '@/navigation/types';
import { common, designSystem, withOpacity } from '@/styles/styles';
import { GameType, League } from '@/types/api.generated';
import { isWeb, nativeDriverValue, showScrollIndicator } from '@/utils/constants-platform-specific';
import { gameUtils } from '@/utils/games';
import { PicksByEvent, groupPicksByEvents } from '@/utils/map-to-events';
import { BottomSheetModal } from '@gorhom/bottom-sheet';

import { EntryFooter } from '../components/EntryFooter';
import { EntryHeader } from '../components/EntryHeader';
import { useEntryLiveUpdates } from '../hooks/use-entry-live-updates';
import { useScreenshotAware } from '../hooks/use-screenshot-aware';

type ScreenProps = NativeStackScreenProps<RootStackParamList, 'EntryScreen'>;

/**
 * SectionList cells are separate native views, so zIndex doesn't work across cells. We wrap at the cell level so header cells can sit above item cells.
 */
const CellRendererComponent = ({ children, style, ...props }: CellRendererProps<unknown>) => {
    const isHeader = String(props.cellKey ?? '').includes('header');
    return (
        <View {...props} style={[style, isHeader ? styles.cellRendererComponent : undefined]}>
            {children}
        </View>
    );
};

export const EntryScreen = ({ route: { params }, navigation }: ScreenProps) => {
    const scrollRef = useRef(new Animated.Value(0)).current;
    const {
        entry,
        eventsMap,
        eventsDataLoaded,
        eventsEnded,
        leaguesUpcomingEvents,
        entryPayout,
        dynamicMultipliers,
        dynamicBoostedMultiplier,
        liveEventIds,
        reload,
        fetching,
    } = useEntryDetails(params.id);

    const picks = useMemo(() => entry?.picks ?? [], [entry?.picks]);
    const sections = useMemo(() => groupPicksByEvents(picks, entry?.edgeCombos ?? []), [entry?.edgeCombos, picks]);
    const isLoading = !entry || !Array.isArray(picks) ? fetching : false;
    const lobbyActions = useLobbySectionSelection(state => state.actions);
    const cashedOutEntry = usePickemCashedOutEntries(s => s.entries.get(entry?.id ?? '')) ?? null;
    const winningEntry = isEntryWin(entry) && !cashedOutEntry;

    const dynamicPayoutSheet = useRef<BottomSheetModal>(null);
    const showLeaderboards = useMemo(
        () => entry?.gameType === GameType.P2p && !!entry.id,
        [entry?.gameType, entry?.id]
    );

    const showCancelButton = entry?.canBeCanceled;
    const canCashOut = entry?.canBeCashedout;

    const showEntryActions = showCancelButton || showLeaderboards || canCashOut;

    const safeInsets = useSafeAreaInsets();
    const eventsInfo = useMemo(() => Object.values(eventsMap), [eventsMap]);
    const picksReady = eventsDataLoaded && (eventsEnded || !!leaguesUpcomingEvents);
    const firstLiveEventId = eventsInfo.find(event => {
        return gameUtils.isLive(event);
    })?.id;

    const goToShareEntryScreen = useCallback(() => {
        navigation.navigate('EntryShare', {
            id: entry?.id,
        });
    }, [entry?.id, navigation]);

    useScreenshotAware(goToShareEntryScreen);

    useResumeEffect(reload);

    useEntryLiveUpdates({ liveEventIds, entryMarkets: picks ?? [], onUpdate: reload });

    const { width, height: windowHeight } = useWindowDimensions();
    const gradientStyle = getRadialGradientStyle(width);

    const openPayoutSheet = useCallback(() => {
        dynamicPayoutSheet.current?.present();
    }, []);

    const gradientOpacity = scrollRef.interpolate({
        inputRange: [0, 100],
        outputRange: [1, 0],
        extrapolate: 'clamp',
    });

    const refreshControl = useMemo(() => {
        return (
            <RefreshControl
                refreshing={isLoading}
                onRefresh={reload}
                colors={[designSystem.colors.neutral700]}
                tintColor={ds.colorValue.icon.primary}
            />
        );
    }, [isLoading, reload]);

    const onScroll = useMemo(() => {
        return Animated.event([{ nativeEvent: { contentOffset: { y: scrollRef } } }], {
            useNativeDriver: nativeDriverValue,
        });
    }, [scrollRef]);

    const activeEventIds = useMemo(
        () => new Set(leaguesUpcomingEvents?.getUpcomingEventsV2.map(e => e.id) ?? []),
        [leaguesUpcomingEvents?.getUpcomingEventsV2]
    );

    const navigateToLobby = useCallback(
        (league: League) => {
            navigation.popTo('PickemHome', {
                screen: 'Lobby',
                params: {
                    screen: 'LobbyScreen',
                    params: { league },
                },
            });
        },
        [navigation]
    );

    const goToEventInLobby = useCallback(
        (league: League, eventId: string) => {
            BetrAnalytics.trackEvent(AnalyticsEvent.VIEW_FULL_EVENT, { league, eventId });
            lobbyActions.setSelectedTile(league);
            lobbyActions.setEventId(eventId);
            navigateToLobby(league);
        },
        [lobbyActions, navigateToLobby]
    );

    const renderItem = useCallback(
        ({ item, index, section }: { item: PickInfoFragment; index: number; section: PicksByEvent }) => {
            const isFirst = index === 0;
            const isLast = index + 1 === section.data.length;
            const nextPlayer = section.data[index + 1];
            const isEdgeCombo = playersHaveEdgeCombo(entry?.edgeCombos ?? [], item.player, nextPlayer?.player);
            const event = eventsMap[item.eventId];
            let edgeComboData;
            if (isEdgeCombo) {
                edgeComboData = [
                    { pick: item, event },
                    { pick: nextPlayer, event: eventsMap[nextPlayer.eventId] },
                ];
            }
            const team = eventUtils.getTeamById(event, item.teamId);

            return (
                <EntryPickPlayerCard
                    key={item.teamId}
                    team={team}
                    eventLeague={event?.league}
                    animateProgressBars={true}
                    hideCrossedOutValue
                    isLast={isLast}
                    isFirst={isFirst}
                    {...item}
                    isEdgeCombo={isEdgeCombo}
                    edgeComboData={edgeComboData}
                    backgroundColor={ds.colorValue.background.neutral.subtler}
                    imageBackgroundColor={ds.colorToken.background.neutral.subtle}
                    borderColor={ds.colorValue.border.subtle}
                />
            );
        },
        [entry?.edgeCombos, eventsMap]
    );

    const renderSectionHeader = useCallback(
        ({ section }: { section: PicksByEvent }) => {
            const event = eventsMap[section.eventId];
            const isActiveEvent = !!event && !gameUtils.hasEnded(event.status) && activeEventIds.has(event.id);

            return (
                <EntrySectionEventHeader
                    event={event}
                    status={entry?.status}
                    showGameTrackerTooltip={section.eventId === firstLiveEventId}
                    onViewFullEvent={isActiveEvent ? () => goToEventInLobby(event.league, event.id) : undefined}
                />
            );
        },
        [activeEventIds, entry?.status, eventsMap, firstLiveEventId, goToEventInLobby]
    );

    const listHeaderComponent = useMemo(
        () => (
            <EntryHeader
                entry={entry}
                entryPayout={entryPayout}
                isLoading={isLoading}
                openPayoutSheet={openPayoutSheet}
            />
        ),
        [entry, entryPayout, isLoading, openPayoutSheet]
    );

    const listFooterComponent = useMemo(() => <EntryFooter entry={entry} events={eventsInfo} />, [entry, eventsInfo]);

    const contentContainerStyle = useMemo(() => {
        return [
            // padding prevents last item from hiding behind the bottom action tray (cancel or cash-out)
            // we need to take into account: safe area + tray vertical padding (2×16) + button height (44)
            (showCancelButton || canCashOut) && { paddingBottom: safeInsets.bottom + 2 * 16 + 44 },
            webListContentContainerStyle,
        ];
    }, [canCashOut, safeInsets.bottom, showCancelButton]);

    return (
        <SheetScreen>
            {winningEntry ? (
                <Animated.View style={[styles.gradient, isWeb && gradientStyle, { opacity: gradientOpacity }]}>
                    <Box alignItems={isWeb ? 'center' : 'flex-start'}>
                        <Gradient
                            color={withOpacity(ds.colorValue.background.success.strong, 0.4)}
                            style={[gradientStyle]}
                        />
                    </Box>
                </Animated.View>
            ) : null}
            <Box height={isWeb ? windowHeight : '100%'}>
                <KeyboardAvoidingViewNoAnimation
                    behavior={Platform.OS === 'ios' ? 'padding' : undefined}
                    style={common.flex}
                    keyboardVerticalOffset={Platform.OS === 'ios' ? 60 : 0}
                >
                    <SizedBox value={10} />
                    <EntryHeaderActions entry={entry} />

                    {
                        // there's a bug in section list where it doesn't render the next page when scrolling
                        // not rendering the list when there's no data somehow fixes it: https://github.com/facebook/react-native/issues/39421
                        sections?.length > 0 && picksReady ? (
                            <Animated.SectionList
                                onScroll={onScroll}
                                style={common.paddingHorizontal}
                                contentContainerStyle={contentContainerStyle}
                                CellRendererComponent={isWeb ? CellRendererComponentForWeb : CellRendererComponent}
                                sections={sections}
                                maxToRenderPerBatch={3}
                                initialNumToRender={3}
                                stickySectionHeadersEnabled={false}
                                showsVerticalScrollIndicator={showScrollIndicator}
                                refreshControl={refreshControl}
                                ListHeaderComponent={listHeaderComponent}
                                ListFooterComponent={listFooterComponent}
                                ListHeaderComponentStyle={webMaxWidthStyle}
                                ListFooterComponentStyle={webMaxWidthStyle}
                                renderSectionHeader={renderSectionHeader}
                                renderItem={renderItem}
                            />
                        ) : null
                    }
                </KeyboardAvoidingViewNoAnimation>
                {showEntryActions ? (
                    <EntryActions entry={entry} events={eventsInfo} onCancelExpired={canCashOut ? reload : undefined} />
                ) : null}
                <DynamicPayoutSheet
                    payoutSheetRef={dynamicPayoutSheet}
                    entryAmount={entry?.amount ?? 0}
                    dynamicMultipliers={dynamicMultipliers ?? []}
                    dynamicBoostedMultiplier={dynamicBoostedMultiplier}
                />
            </Box>
        </SheetScreen>
    );
};

const styles = StyleSheet.create({
    gradient: { height: 216, width: gradientWidth, position: 'absolute', top: -35 },
    contentContainer: {
        position: 'absolute',
        width: '100%',
    },
    cellRendererComponent: {
        zIndex: 10,
        overflow: 'visible',
    },
});
